import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Star, Quote, ArrowUpRight, MessageSquareHeart, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function initials(name) {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    const first = parts[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1] : "";
    return (first[0] || "") + (last[0] ? last[0] + "." : ".");
}

function relTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch {
        return "";
    }
}

export default function Reviews() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/testimonials`);
                if (alive) setData(r.data);
            } catch {
                if (alive) setData({ items: [], total: 0 });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const items = data?.items || [];

    return (
        <div data-testid="reviews-page" className="bg-white">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0B1120] text-white tr-grain">
                <div className="absolute -top-32 left-1/4 w-[520px] h-[520px] tr-glow bg-cyan-500/30" />
                <div className="absolute -bottom-20 -right-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline text-cyan-400 mb-4">· Verified clinic feedback</div>
                        <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98]">
                            What clinicians say<br />
                            <span className="italic font-extralight text-cyan-300/90">about our lab.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-white/70 text-[15px] leading-[1.75]">
                            Every quote here was submitted through our NPS form by a real clinic
                            after a real case was delivered. Names are redacted to first-initial,
                            case numbers to the last 4 digits.
                        </p>
                    </div>
                </div>
            </section>

            {/* STATS STRIP */}
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <Stat label="Published reviews" value={data?.total ?? "—"} icon={MessageSquareHeart} />
                    <Stat label="Avg star rating" value={items.length ? avgStars(items) : "—"} icon={Star} suffix=" / 5" />
                    <Stat label="Warranty" value="10 yr" icon={Sparkles} sub="every restoration" />
                    <Stat label="Anonymised" value="100%" icon={Quote} sub="opt-in only" />
                </div>
            </section>

            {/* GRID */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20" data-testid="reviews-grid">
                {loading ? (
                    <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                ) : items.length === 0 ? (
                    <EmptyState />
                ) : (
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
                        {items.map((it, i) => (
                            <article
                                key={it.id}
                                data-testid={`review-card-${i}`}
                                className="break-inside-avoid mb-6 rounded-[20px] border border-slate-200 bg-white p-6 lg:p-7 hover:border-cyan-300 hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex items-center gap-1 mb-3">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star key={s} size={14} className={s <= avgStarsFor(it) ? "text-amber-500 fill-amber-500" : "text-slate-200"} />
                                    ))}
                                </div>
                                <Quote size={20} className="text-cyan-500/70 mb-3" strokeWidth={1.25} />
                                <p className="text-[15px] text-slate-700 leading-[1.75] font-light">
                                    "{it.comment}"
                                </p>
                                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-white flex items-center justify-center text-[12px] font-bold">
                                        {initials(it.name) || "·"}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13.5px] font-medium text-slate-900 truncate">
                                            {it.name ? initials(it.name) : "Anonymous"} <span className="text-slate-400 font-normal">· {it.role || "clinic"}</span>
                                        </div>
                                        <div className="text-[11px] font-mono-tr tracking-wider text-slate-400">
                                            Case {it.case_ref || "—"} · {relTime(it.submitted_at)}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            {/* CTA */}
            <section className="border-t border-slate-200 bg-slate-50/60">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                        <div className="tr-overline mb-3">· Worked with us recently?</div>
                        <h3 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                            Share your experience.<br />
                            <span className="italic text-slate-500">Help a fellow clinician choose well.</span>
                        </h3>
                    </div>
                    <div className="lg:col-span-4 lg:flex lg:justify-end">
                        <Link
                            to="/feedback"
                            data-testid="reviews-leave-feedback"
                            className="tr-btn !py-4 !px-6"
                        >
                            Leave feedback <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function avgStarsFor(it) {
    const r = [it.fit_rating, it.aesthetics_rating, it.delivery_rating].filter(Boolean);
    if (!r.length) return 5; // promoter with no sub-ratings → display 5★
    return Math.round(r.reduce((a, b) => a + b, 0) / r.length);
}

function avgStars(items) {
    const all = items.map((it) => avgStarsFor(it));
    const sum = all.reduce((a, b) => a + b, 0);
    return (sum / all.length).toFixed(1);
}

function Stat({ label, value, sub, suffix, icon: Icon }) {
    return (
        <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-cyan-600">
                <Icon size={16} />
            </div>
            <div>
                <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">{label}</div>
                <div className="mt-0.5 font-display font-light text-2xl tracking-tight text-slate-900">{value}{suffix}</div>
                {sub && <div className="text-[11.5px] text-slate-500">{sub}</div>}
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div data-testid="reviews-empty" className="text-center py-20 max-w-xl mx-auto">
            <Quote size={48} className="text-slate-300 mx-auto" strokeWidth={1.25} />
            <h2 className="mt-6 font-display font-light text-3xl tracking-tight text-slate-900">
                The wall is warming up.
            </h2>
            <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">
                Reviews appear here as soon as clinics opt in via the post-delivery NPS form.
                Recently worked with us? You can be the first.
            </p>
            <Link to="/feedback" className="tr-btn mt-8 inline-flex">
                Leave feedback <ArrowUpRight size={16} />
            </Link>
        </div>
    );
}
